import { Building, BuildingCategory } from './constants';

// Input command types for the game
export enum InputCommandType {
  // Movement commands
  MOVE = 'MOVE',

  // Action commands
  TAKE_ACTION = 'TAKE_ACTION',

  // Building commands
  BUILD_DECONSTRUCT = 'BUILD_DECONSTRUCT',

  // Resource commands
  COLLECT_RESOURCE = 'COLLECT_RESOURCE',

  // UI commands
  TOGGLE_BUILD_MENU = 'TOGGLE_BUILD_MENU',
  SELECT_BUILD_CATEGORY = 'SELECT_BUILD_CATEGORY',
  SELECT_BUILDING = 'SELECT_BUILDING',
  TOGGLE_SAVE_MENU = 'TOGGLE_SAVE_MENU',
  TOGGLE_DEBUG_MENU = 'TOGGLE_DEBUG_MENU',
  TOGGLE_HELP_MENU = 'TOGGLE_HELP_MENU',
  TOGGLE_LEFT_PANEL = 'TOGGLE_LEFT_PANEL',
  TOGGLE_ACHIEVEMENT_MENU = 'TOGGLE_ACHIEVEMENT_MENU',
  TOGGLE_RESEARCH_MENU = 'TOGGLE_RESEARCH_MENU',
  TOGGLE_PRESTIGE_MENU = 'TOGGLE_PRESTIGE_MENU',
  TOGGLE_PRODUCTION_MENU = 'TOGGLE_PRODUCTION_MENU',
  TOGGLE_INVENTORY = 'TOGGLE_INVENTORY',
  CLOSE_ALL_MENUS = 'CLOSE_ALL_MENUS',

  // Camera commands
  ZOOM_IN = 'ZOOM_IN',
  ZOOM_OUT = 'ZOOM_OUT',
  ZOOM_RESET = 'ZOOM_RESET',

  // Game commands
  SAVE_GAME = 'SAVE_GAME',
  CLEAR_BELT_ITEMS = 'CLEAR_BELT_ITEMS',
}

// Base interface for all input commands
export interface BaseInputCommand {
  type: InputCommandType;
  timestamp: number;
  repeat?: boolean; // Whether this command should repeat while key is held
}

// Movement command with direction
export interface MoveCommand extends BaseInputCommand {
  type: InputCommandType.MOVE;
  direction: { x: number; y: number };
  repeat: true; // Movement always repeats
  isRepeat: boolean; // Whether this command is from a keyboard repeat event
}

// Take action command - contextual action based on game state
export interface TakeActionCommand extends BaseInputCommand {
  type: InputCommandType.TAKE_ACTION;
  shiftKey?: boolean; // Whether shift was held (for continuous placement)
  target?: { x: number; y: number }; // Optional target override (e.g., from mouse interaction)
}

// Building deconstruction command
export interface BuildDeconstructCommand extends BaseInputCommand {
  type: InputCommandType.BUILD_DECONSTRUCT;
  position?: { x: number; y: number }; // Optional - may be determined by target position
}

// Resource collection command
export interface CollectResourceCommand extends BaseInputCommand {
  type: InputCommandType.COLLECT_RESOURCE;
  position?: { x: number; y: number };
}

// UI toggle commands
export interface ToggleUICommand extends BaseInputCommand {
  type:
    | InputCommandType.TOGGLE_BUILD_MENU
    | InputCommandType.TOGGLE_SAVE_MENU
    | InputCommandType.TOGGLE_DEBUG_MENU
    | InputCommandType.TOGGLE_HELP_MENU
    | InputCommandType.TOGGLE_LEFT_PANEL
    | InputCommandType.TOGGLE_ACHIEVEMENT_MENU
    | InputCommandType.TOGGLE_RESEARCH_MENU
    | InputCommandType.TOGGLE_PRESTIGE_MENU
    | InputCommandType.TOGGLE_PRODUCTION_MENU
    | InputCommandType.TOGGLE_INVENTORY
    | InputCommandType.CLOSE_ALL_MENUS;
}

// Build category selection command
export interface SelectBuildCategoryCommand extends BaseInputCommand {
  type: InputCommandType.SELECT_BUILD_CATEGORY;
  category: BuildingCategory | null;
}

// Building selection command
export interface SelectBuildingCommand extends BaseInputCommand {
  type: InputCommandType.SELECT_BUILDING;
  building: Building;
}

// Camera control commands
export interface CameraCommand extends BaseInputCommand {
  type:
    | InputCommandType.ZOOM_IN
    | InputCommandType.ZOOM_OUT
    | InputCommandType.ZOOM_RESET;
  repeat?: boolean; // Zoom commands can repeat
}

// Simple action commands
export interface SimpleActionCommand extends BaseInputCommand {
  type: InputCommandType.SAVE_GAME | InputCommandType.CLEAR_BELT_ITEMS;
}

// Union type for all possible input commands
export type InputCommand =
  | MoveCommand
  | TakeActionCommand
  | BuildDeconstructCommand
  | CollectResourceCommand
  | ToggleUICommand
  | SelectBuildCategoryCommand
  | SelectBuildingCommand
  | CameraCommand
  | SimpleActionCommand;

// Input command queue class
export class InputCommandQueue {
  private commands: InputCommand[] = [];

  // Add a command to the queue
  enqueue(command: InputCommand): void {
    this.commands.push(command);
  }

  // Get all commands and clear the queue
  dequeueAll(): InputCommand[] {
    const allCommands = [...this.commands];
    this.commands.length = 0;
    return allCommands;
  }

  // Peek at commands without removing them
  peek(): readonly InputCommand[] {
    return this.commands;
  }

  // Check if queue is empty
  isEmpty(): boolean {
    return this.commands.length === 0;
  }

  // Clear all commands
  clear(): void {
    this.commands.length = 0;
  }
}

// Helper functions for creating commands
export const createMoveCommand = (
  x: number,
  y: number,
  isRepeat = false
): MoveCommand => ({
  type: InputCommandType.MOVE,
  direction: { x, y },
  timestamp: performance.now(),
  repeat: true,
  isRepeat,
});

export const createTakeActionCommand = ({
  shiftKey = false,
  target,
}: {
  shiftKey?: boolean;
  target?: { x: number; y: number };
} = {}): TakeActionCommand => ({
  type: InputCommandType.TAKE_ACTION,
  timestamp: performance.now(),
  shiftKey,
  ...(target ? { target } : {}),
});

export const createBuildDeconstructCommand = (position?: {
  x: number;
  y: number;
}): BuildDeconstructCommand => {
  const command: BuildDeconstructCommand = {
    type: InputCommandType.BUILD_DECONSTRUCT,
    timestamp: performance.now(),
  };

  if (position !== undefined) {
    command.position = position;
  }

  return command;
};

export const createCollectResourceCommand = ({
  position,
  repeat = false,
}: {
  position?: { x: number; y: number };
  repeat?: boolean;
} = {}): CollectResourceCommand => ({
  type: InputCommandType.COLLECT_RESOURCE,
  timestamp: performance.now(),
  ...(position ? { position } : {}),
  ...(repeat ? { repeat: true } : {}),
});

export const createToggleUICommand = (
  type: ToggleUICommand['type']
): ToggleUICommand => ({
  type,
  timestamp: performance.now(),
});

export const createCameraCommand = (
  type: CameraCommand['type'],
  repeat = false
): CameraCommand => ({
  type,
  repeat,
  timestamp: performance.now(),
});

export const createSelectBuildCategoryCommand = (
  category: BuildingCategory | null
): SelectBuildCategoryCommand => ({
  type: InputCommandType.SELECT_BUILD_CATEGORY,
  category,
  timestamp: performance.now(),
});

export const createSelectBuildingCommand = (
  building: Building
): SelectBuildingCommand => ({
  type: InputCommandType.SELECT_BUILDING,
  building,
  timestamp: performance.now(),
});

export const createSimpleActionCommand = (
  type: SimpleActionCommand['type']
): SimpleActionCommand => ({
  type,
  timestamp: performance.now(),
});
